/*
 * Real-time discount progress bar. Used by both the PDP and the cart drawer.
 *
 * Neither bar refreshes on its own:
 *  - The PDP bar lives outside any cart section, so nothing re-renders it.
 *  - The cart drawer bar sits outside <cart-drawer-items>, and CartItems'
 *    partial refresh in cart.js only replaces ["cart-drawer-items",
 *    ".cart-drawer__footer"] — so an in-drawer upsell add left it stale.
 *
 * This element listens for the theme-wide `cartUpdate` pub/sub event and
 * recomputes the bar client-side — width, badge, reached state and the amount
 * labels — so adding or removing an item anywhere updates it instantly.
 *
 * All configuration comes from the data-* attributes rendered by
 * snippets/discount-progress.liquid — the element never reaches back to Liquid.
 */
if (!customElements.get("discount-progress-bar")) {
  class DiscountProgressBar extends HTMLElement {
    connectedCallback() {
      this.threshold = parseInt(this.dataset.threshold, 10) || 0;
      this.variant = this.dataset.variant || "cart";
      this.locale = this.dataset.locale || "de";
      this.currency = this.dataset.currency || "EUR";
      this.textProgressTpl = this.dataset.textProgress || "";
      this.textReachedTpl = this.dataset.textReached || "";

      this.textEl = this.querySelector(".discount-progress__text");
      this.fillEl = this.querySelector(".discount-progress__fill");
      this.trackEl = this.querySelector(".discount-progress__track");
      this.badgeEl = this.querySelector(".discount-progress__badge");
      this.currentEl = this.querySelector(".discount-progress__current");

      // On product pages the label is positioned server-side from Liquid, which
      // can't measure the label width — so a low, non-zero value can still spill
      // past the card edge on first paint. Re-run the width-aware positioning
      // once on connect, after layout, to contain it.
      if (this.variant === "pdp" && this.threshold > 0) {
        const initialCurrent = parseInt(this.dataset.current, 10) || 0;
        requestAnimationFrame(() => this.positionCurrentLabel(initialCurrent));
      }

      // Nothing to track without a threshold or the pub/sub helpers.
      if (this.threshold <= 0 || typeof subscribe !== "function") return;

      this.unsubscribe = subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        this.onCartUpdate.bind(this),
      );
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    onCartUpdate(event) {
      // The pub/sub bus runs subscribers in a plain forEach with no error
      // isolation, so a throw here would stop later subscribers (e.g. the cart
      // itself) from running. Never let this handler break other cart code.
      try {
        // Cart-drawer quantity changes ship the full cart (total_price +
        // item_count); add-to-cart responses don't, so fall back to a
        // lightweight /cart.js read to get both.
        const data = event && event.cartData;
        if (
          data &&
          typeof data.total_price === "number" &&
          typeof data.item_count === "number"
        ) {
          this.render(data);
          return;
        }

        const base = (window.routes && window.routes.cart_url) || "/cart";
        fetch(`${base}.js`, { headers: { Accept: "application/json" } })
          .then((response) => response.json())
          .then((cart) => this.render(cart))
          .catch(() => {});
      } catch (error) {
        // Swallow: a broken progress bar must not affect cart behaviour.
      }
    }

    formatMoney(cents) {
      const whole = cents % 100 === 0;
      try {
        return new Intl.NumberFormat(this.locale, {
          style: "currency",
          currency: this.currency,
          minimumFractionDigits: whole ? 0 : 2,
          maximumFractionDigits: whole ? 0 : 2,
        }).format(cents / 100);
      } catch (error) {
        return (cents / 100).toFixed(whole ? 0 : 2);
      }
    }

    render(cart) {
      // Prefer item_count over the total so a cart holding only free items
      // still counts as non-empty.
      const itemCount =
        cart && typeof cart.item_count === "number" ? cart.item_count : null;
      const current = (cart && cart.total_price) || 0;
      const isEmpty = itemCount !== null ? itemCount === 0 : current <= 0;

      // The cart-drawer bar hides on an empty cart; the PDP bar always stays
      // visible (showing the full amount still needed to reach the goal), so an
      // empty-cart update never removes it — it just resets to 0.
      const hideWhenEmpty = this.variant !== "pdp";
      this.hidden = isEmpty && hideWhenEmpty;
      if (isEmpty && hideWhenEmpty) return;

      const remaining = this.threshold - current;
      let progress = (current * 100) / this.threshold;
      if (progress > 100) progress = 100;
      if (progress < 0) progress = 0;
      const reached = remaining <= 0;

      if (this.fillEl) this.fillEl.style.width = `${progress}%`;
      if (this.trackEl) {
        this.trackEl.setAttribute("aria-valuenow", `${Math.round(progress)}`);
      }

      this.classList.toggle("discount-progress--reached", reached);
      if (this.badgeEl) {
        this.badgeEl.classList.toggle(
          "discount-progress__badge--reached",
          reached,
        );
      }

      if (this.textEl) {
        const money = this.formatMoney(remaining);
        this.textEl.innerHTML = reached
          ? this.textReachedTpl
          : this.textProgressTpl.replace("__AMOUNT__", () => money);
      }

      this.positionCurrentLabel(current);
    }

    // Places (or hides) the moving "current amount" label under the fill.
    // The label is centred on the progress point but kept fully inside the
    // card: never past the goal label on the right, never spilling off the
    // left. Hidden when the goal is reached (the pinned goal label already
    // shows the amount) or when the cart is still empty.
    positionCurrentLabel(current) {
      const label = this.currentEl;
      if (!label) return;

      if (current <= 0 || current >= this.threshold) {
        label.style.display = "none";
        return;
      }

      label.style.display = "";
      label.textContent = this.formatMoney(current);

      let progress = (current * 100) / this.threshold;
      if (progress > 100) progress = 100;
      if (progress < 0) progress = 0;

      // Right cap so the label never slides under the goal label. Keep in sync
      // with the clamp in discount-progress.liquid.
      const MAX_CENTER_PERCENT = 68;

      const container = label.parentElement;
      const cw = container ? container.offsetWidth : 0;
      const lw = label.offsetWidth;

      if (!cw || !lw) {
        // Layout not ready, or this is the off-breakpoint copy (display:none) —
        // fall back to the percentage clamp without pixel measurement.
        label.style.left = `${Math.min(progress, MAX_CENTER_PERCENT)}%`;
        return;
      }

      const half = lw / 2;
      const PAD = 4; // breathing room from the card's inner edge
      let centerPx = (progress / 100) * cw;
      centerPx = Math.min(centerPx, (MAX_CENTER_PERCENT / 100) * cw);
      centerPx = Math.min(Math.max(centerPx, half + PAD), cw - half - PAD);
      label.style.left = `${(centerPx / cw) * 100}%`;
    }
  }

  customElements.define("discount-progress-bar", DiscountProgressBar);
}
